import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Building2, Loader2 } from "lucide-react";

export function CompanySettings() {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ["company-settings"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("company_settings")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
  });

  const [formData, setFormData] = useState({
    company_name: "",
    company_email: "",
    company_phone: "",
    company_address: "",
    company_city: "",
    company_country: "",
    company_zip: "",
    tax_id: "",
    currency: "USD",
    invoice_prefix: "INV",
    invoice_next_number: 1,
    invoice_notes: "",
    invoice_terms: "",
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        company_name: settings.company_name || "",
        company_email: settings.company_email || "",
        company_phone: settings.company_phone || "",
        company_address: settings.company_address || "",
        company_city: settings.company_city || "",
        company_country: settings.company_country || "",
        company_zip: settings.company_zip || "",
        tax_id: settings.tax_id || "",
        currency: settings.currency || "USD",
        invoice_prefix: settings.invoice_prefix || "INV",
        invoice_next_number: settings.invoice_next_number || 1,
        invoice_notes: settings.invoice_notes || "",
        invoice_terms: settings.invoice_terms || "",
      });
    }
  }, [settings]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      if (settings) {
        // Update existing
        const { error } = await supabase
          .from("company_settings")
          .update(formData)
          .eq("user_id", user.id);

        if (error) throw error;
      } else {
        // Create new
        const { error } = await supabase
          .from("company_settings")
          .insert({ ...formData, user_id: user.id });

        if (error) throw error;
      }

      queryClient.invalidateQueries({ queryKey: ["company-settings"] });
      toast({
        title: "Success",
        description: "Company settings saved successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Company Information
        </CardTitle>
        <CardDescription>
          This information will be used in invoices and official documents
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="company_name">Company Name *</Label>
            <Input
              id="company_name"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              placeholder="Your Company Name"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company_email">Company Email</Label>
            <Input
              id="company_email"
              type="email"
              value={formData.company_email}
              onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
              placeholder="contact@company.com"
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="company_phone">Company Phone</Label>
            <Input
              id="company_phone"
              value={formData.company_phone}
              onChange={(e) => setFormData({ ...formData, company_phone: e.target.value })}
              placeholder="+1 234 567 8900"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tax_id">Tax ID / VAT Number</Label>
            <Input
              id="tax_id"
              value={formData.tax_id}
              onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
              placeholder="TAX-123456"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="company_address">Address</Label>
          <Input
            id="company_address"
            value={formData.company_address}
            onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
            placeholder="123 Business Street"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="company_city">City</Label>
            <Input
              id="company_city"
              value={formData.company_city}
              onChange={(e) => setFormData({ ...formData, company_city: e.target.value })}
              placeholder="New York"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company_country">Country</Label>
            <Input
              id="company_country"
              value={formData.company_country}
              onChange={(e) => setFormData({ ...formData, company_country: e.target.value })}
              placeholder="United States"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company_zip">ZIP Code</Label>
            <Input
              id="company_zip"
              value={formData.company_zip}
              onChange={(e) => setFormData({ ...formData, company_zip: e.target.value })}
              placeholder="10001"
            />
          </div>
        </div>

        <div className="border-t pt-6 mt-6">
          <h3 className="text-lg font-semibold mb-4">Invoice Settings</h3>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="invoice_prefix">Invoice Prefix</Label>
              <Input
                id="invoice_prefix"
                value={formData.invoice_prefix}
                onChange={(e) => setFormData({ ...formData, invoice_prefix: e.target.value })}
                placeholder="INV"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoice_next_number">Next Invoice Number</Label>
              <Input
                id="invoice_next_number"
                type="number"
                value={formData.invoice_next_number}
                onChange={(e) => setFormData({ ...formData, invoice_next_number: parseInt(e.target.value) })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Input
                id="currency"
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                placeholder="USD"
              />
            </div>
          </div>

          <div className="space-y-2 mt-4">
            <Label htmlFor="invoice_notes">Default Invoice Notes</Label>
            <Textarea
              id="invoice_notes"
              value={formData.invoice_notes}
              onChange={(e) => setFormData({ ...formData, invoice_notes: e.target.value })}
              placeholder="Thank you for your business!"
              rows={3}
            />
          </div>

          <div className="space-y-2 mt-4">
            <Label htmlFor="invoice_terms">Default Payment Terms</Label>
            <Textarea
              id="invoice_terms"
              value={formData.invoice_terms}
              onChange={(e) => setFormData({ ...formData, invoice_terms: e.target.value })}
              placeholder="Payment due within 30 days"
              rows={3}
            />
          </div>
        </div>

        <Button onClick={handleSave} disabled={loading} className="w-full">
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save Company Settings"
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
